import {cn} from '@/lib/utils'
import {Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Alert, AlertDescription, AlertTitle} from '@/components/ui/alert'
import {Loader2Icon, CircleAlertIcon, ThumbsUpIcon, ThumbsDownIcon} from 'lucide-react'
import {Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis} from 'recharts'
import {ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig} from '@/components/ui/chart'
import {NavLinks} from '@/components/nav-links'
import {NumberCard} from '@/components/number-card'
import {useAuth} from '@/hooks/auth-provider'
import {useLocalStorage} from '@/hooks/use-local-storage'
import useSWR, {type Fetcher} from 'swr'
import dayjs from 'dayjs'
import type {PhotoData, SightingData} from '@/types'

const chartConfig: ChartConfig = {
  engaged: {
    label: 'Engaged',
    color: '#69d2e7',
  },
  template_selected: {
    label: 'Template Selected',
    color: '#a7dbd8',
  },
  payment_started: {
    label: 'Payment Started',
    color: '#e0e4cc',
  },
  payment_completed: {
    label: 'Payment Completed',
    color: '#f38630',
  },
  generated: {
    label: 'Generated',
    color: '#fa6900',
  },
}

function FunnelBar({dataKey, extraLabel}: {dataKey: string; extraLabel?: string}) {
  const label = String(chartConfig[dataKey]!.label) + (extraLabel ? ' ' + extraLabel : '')
  return (
    <Bar dataKey={dataKey} fill={`var(--color-${dataKey})`} radius={4}>
      <LabelList valueAccessor={() => label} position='insideLeft' offset={8} className='fill-background' fontSize={12} />
      <LabelList dataKey={dataKey} position='right' offset={8} className='fill-foreground' fontSize={12} />
    </Bar>
  )
}

export function Charts({className, ...props}: React.ComponentProps<'div'>) {
  const [period, setPeriod] = useLocalStorage('period', 'day')
  const [booth, setBooth] = useLocalStorage('booth', 'all')

  const auth = useAuth()

  const photoFetcher: Fetcher<PhotoData[], string> = (url: string, init = {}) =>
    fetch(url, {...init, headers: auth.header})
      .then(res => res.json())
      .then(res => (res.data ? res.data : Promise.reject(res.error)))

  const sightingFetcher: Fetcher<SightingData[], string> = (url: string, init = {}) =>
    fetch(url, {...init, headers: auth.header})
      .then(res => res.json())
      .then(res => (res.data ? res.data : Promise.reject(res.error)))

  const {data: photos, error, isLoading} = useSWR(`/api/photos?booth=${booth}&period=${period}`, photoFetcher, {refreshInterval: 60000})
  const {data: sightings} = useSWR(`/api/sightings?booth=${booth}&period=${period}`, sightingFetcher, {refreshInterval: 60000})

  const byDay = /^month/.test(period || '')
  const interval = byDay ? 24 * 60 * 60 * 1000 : 60 * 60 * 1000
  const intervalUnit = byDay ? 'day' : 'hour'
  const boundaryUnit = byDay ? 'week' : 'hour'

  const filtered = (photos || []).map(p => ({
    t: dayjs(p.created_at + 'Z')
      .startOf(intervalUnit)
      .toDate()
      .getTime(),
    status: p.status,
    templates_viewed: p.templates_viewed,
  }))
  const start = dayjs(Math.min(...filtered.map(s => s.t)))
    .startOf(boundaryUnit)
    .toDate()
    .getTime()

  interface DataPoint {
    engaged: number
    template_selected: number
    templates_viewed: number
    payment_started: number
    payment_completed: number
    photo_taken: number
    generated: number
    rated: number
  }
  const emptyDP: DataPoint = {
    engaged: 0,
    template_selected: 0,
    templates_viewed: 0,
    payment_started: 0,
    payment_completed: 0,
    photo_taken: 0,
    generated: 0,
    rated: 0,
  }
  const buckets: DataPoint[] = []
  const total: DataPoint = {...emptyDP}

  for (const p of filtered) {
    const offset = ((p.t - start) / interval) | 0
    const dp = buckets[offset] || {...emptyDP}
    if (p.status >= '00_engaged') {
      dp.engaged++
      total.engaged++
    }
    if (p.status >= '10_template_selected') {
      dp.template_selected++
      total.template_selected++
    }
    dp.templates_viewed += p.templates_viewed || 0
    total.templates_viewed++

    if (p.status >= '20_payment_started') {
      dp.payment_started++
      total.payment_started++
    }
    if (p.status >= '30_payment_completed') {
      dp.payment_completed++
      total.payment_completed++
    }
    if (p.status >= '40_photo_taken') {
      dp.photo_taken++
      total.photo_taken++
    }
    if (p.status >= '50_generated') {
      dp.generated++
      total.generated++
    }
    if (p.status >= '60_rated') {
      dp.rated++
      total.rated++
    }
    buckets[offset] = dp
  }

  const chartData = [...buckets].map((dp, i) => ({t: i * interval + start, ...(dp || emptyDP)}))

  const totalSightings = (sightings || []).reduce((acc, s) => acc + s.count, 0)

  if (isLoading)
    return (
      <div className={className} {...props}>
        <Loader2Icon />
      </div>
    )

  if (error) {
    return (
      <Alert>
        <CircleAlertIcon />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className={cn('m-6 flex flex-col gap-6', className)} {...props}>
      <div className='flex flex-row flex-wrap gap-1 lg:gap-6'>
        <NavLinks />
        <Select defaultValue={period || 'day'} onValueChange={setPeriod}>
          <SelectTrigger className='w-full lg:max-w-48'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>Time Period</SelectLabel>
              <SelectItem value='day'>Today</SelectItem>
              <SelectItem value='day-1'>Yesterday</SelectItem>
              <SelectItem value='month'>This month</SelectItem>
              <SelectItem value='month-1'>Last month</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
        <Select defaultValue={booth || ''} onValueChange={setBooth}>
          <SelectTrigger className='w-full lg:max-w-48'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>Booth</SelectLabel>
              <SelectItem value='all'>All booths</SelectItem>
              <SelectItem value='planeta'>Planeta</SelectItem>
              <SelectItem value='park'>Park</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div className='flex flex-row flex-wrap gap-6'>
        <NumberCard
          className='grow-1 text-8xl'
          title='Sales'
          value={photos!.reduce((c, p) => c + (p.status >= '30_payment_completed' ? 1 : 0), 0)}
        />
        <NumberCard className='grow-1 text-8xl' title='Engagements' value={photos!.length} />
        <Card className='grow-3 min-w-100 sm:w-100 p-0'>
          <CardContent>
            <ChartContainer className='w-full h-80 aspect-auto' config={chartConfig}>
              <BarChart accessibilityLayer layout='vertical' data={[total]}>
                <CartesianGrid horizontal={false} />
                <XAxis dataKey='engaged' type='number' hide />
                <YAxis type='category' hide />
                <FunnelBar
                  dataKey='engaged'
                  extraLabel={totalSightings ? '(' + ((total.engaged * 100) / totalSightings).toFixed(2) + '%)' : ''}
                />
                <FunnelBar dataKey='template_selected' />
                <FunnelBar dataKey='payment_started' />
                <FunnelBar dataKey='payment_completed' />
                <FunnelBar dataKey='generated' />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
        <div className='grow-1 flex flex-row sm:flex-col gap-6'>
          <NumberCard className='grow-1 text-4xl' title='People seen' value={totalSightings} />
          <Card className='grow-1'>
            <CardHeader>
              <CardTitle className='text-base'>Likes | Dislikes</CardTitle>
            </CardHeader>
            <CardContent className='h-full flex gap-6 place-content-center place-items-center align-center text-4xl'>
              <div className='flex flex-row place-items-center gap-2'>
                <ThumbsUpIcon className='inline-block text-muted-foreground' />
                {photos!.reduce((acc, p) => acc + (p.rating > 0 ? 1 : 0), 0)}
              </div>
              <div className='flex flex-row place-items-center gap-2'>
                <ThumbsDownIcon className='inline-block text-muted-foreground' />
                {photos!.reduce((acc, p) => acc + (p.rating < 0 ? 1 : 0), 0)}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Funnel by {intervalUnit}</CardTitle>
        </CardHeader>
        <ChartContainer className='h-100 aspect-auto w-full' config={chartConfig}>
          <BarChart data={chartData}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey='t'
              tickFormatter={ts => {
                const dt = dayjs(ts)
                return dt.format(dt.hour() === 0 ? 'YYYY-MM-DD' : dt.format('H'))
              }}
            />
            <YAxis allowDecimals={false} padding={{top: 10}} />
            <Bar dataKey='engaged' fill='var(--color-engaged)' radius={4} />
            <Bar dataKey='template_selected' fill='var(--color-template_selected)' radius={4} />
            <Bar dataKey='payment_started' fill='var(--color-payment_started)' radius={4} />
            <Bar dataKey='payment_completed' fill='var(--color-payment_completed)' radius={4} />
            <Bar dataKey='generated' fill='var(--color-generated)' radius={4} />
            <ChartTooltip content={<ChartTooltipContent hideLabel />} />
            {/* <ChartLegend content={<ChartLegendContent />} /> */}
          </BarChart>

          {/* <AreaChart reverseStackOrder={true} data={chartData}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="t"
              tickFormatter={ts => {
                const dt = dayjs(ts)
                return dt.format(dt.hour() === 0 ? 'YYYY-MM-DD' : dt.format('H'))
              }}
            />
            <YAxis />
            <Area stackId="a" type="linear" dataKey="engaged" fill="var(--color-engaged)" stroke="var(--color-engaged)" radius={4} />
            <Area stackId="a" type="linear" dataKey="template_selected" fill="var(--color-template_selected)" stroke="var(--color-template_selected)" radius={4} />
            <Area stackId="a" type="linear" dataKey="payment_started" fill="var(--color-payment_started)" stroke="var(--color-payment_started)" radius={4} />
            <Area stackId="a" type="linear" dataKey="payment_completed" fill="var(--color-payment_completed)" stroke="var(--color-payment_completed)" radius={4} />
            <Area stackId="a" type="linear" dataKey="generated" fill="var(--color-generated)" stroke="var(--color-generated)" radius={4} />
            <ChartTooltip content={<ChartTooltipContent hideLabel />} />
            <ChartLegend content={<ChartLegendContent />} />
          </AreaChart> */}
        </ChartContainer>
      </Card>
    </div>
  )
}
